import { Component, EventEmitter, Input, Output } from '@angular/core';

import * as d3Scale from 'd3-scale';
import * as d3Array from 'd3-array';
import * as d3Shape from 'd3-shape';
import * as d3Axis from 'd3-axis';

import { DataColumnPoint, TooltipData, ICoordinates, Serie, DataColumn } from '../../../models';
import { BaseChartComponent } from '../../rawd3/base';
import { ChartingService } from '../../../services';

@Component({
    selector: 'aa-stacked-column',
    templateUrl: './stacked-column.component.html',
    styleUrls: [
        '../../../container.scss',
        './stacked-column.component.scss'
    ],
    providers: [
        {
            provide: ChartingService,
            useClass: ChartingService
        }
    ],
    standalone: false
})
export class StackedColumnComponent extends BaseChartComponent<DataColumn>
{
    @Input() keys: string[];
    @Input() stacked100: boolean = true;
    @Input() originZero: boolean = true;
    @Output() columnClicked: EventEmitter<any> = new EventEmitter<any>();
    public xScale: d3Scale.ScaleBand<string>;
    public yScale: d3Scale.ScaleLinear<number, number>;
    public yScale1: d3Scale.ScaleLinear<number, number>;
    public zScale: d3Scale.ScaleOrdinal<string, unknown>;
    public tooltipData: TooltipData;
    public hovered: boolean = false;
    private _series: Serie[] = undefined;
    public get series (): Serie[]
    {
        if (this._series === undefined)
        {
            this._series = Serie.getSeriesFromDataColumn(this.data);
            this._series2 = this.series.map((serie: Serie): Serie =>
                ({
                    ...serie,
                    unit: ''
                }));
        }

        return this._series;
    }
    private _series2: Serie[] = undefined;
    public get series2 (): Serie[]
    {
        if (this._series2 === undefined)
        {
            this._series = Serie.getSeriesFromDataColumn(this.data);
            this._series2 = this.series.map((serie: Serie): Serie =>
                ({
                    ...serie,
                    unit: ''
                }));
        }

        return this._series2;
    }
    public tooltipFormatTick: (value: number) => (string) = (value: number): string=> value.toFixed(0);
    private eachBand: number;

    constructor (
        private chartingService: ChartingService
    )
    {
        super();
    }

    protected drawChart (): void
    {
        this.initSvg();

        let fmt: string = 's';
        const t: string = 'total';
        const localData: any[] = this.data.points
            .map((p: DataColumnPoint): { x: string; } =>
            {
                const o = { x: p.x };
                let total = 0;

                for (const [, v] of p.values)
                {
                    total += v;
                }

                for (const [k, v] of p.values)
                {
                    if (this.stacked100)
                    {
                        o[k] = 100 * v / total;
                    }
                    else
                    {
                        o[k] = v;
                    }
                }

                if (this.stacked100)
                {
                    o[t] = 100;
                    fmt = '%';
                }
                else
                {
                    o[t] = total;
                }

                return o;
            });

        this.xScale.domain(localData.map((d: any) => d.x));
        this.eachBand = this.xScale.step();

        if (this.stacked100)
        {
            this.yScale.domain([0, 100]).nice();
            this.yScale1.domain([0, 1]).nice();
        }
        else
        {
            this.yScale
                .domain([0, d3Array.max(localData, (d: any) => d.total)]).nice();
        }

        this.zScale.domain(this.keys);

        this.gpeChart
            .append('g')
            .selectAll('g')
            .data(d3Shape.stack().keys(this.keys)(localData))
            .enter()
            .append('g')
            .attr('fill', (d: any) => this.zScale(d.key))
            .selectAll('rect')
            .data((d: any) => d)
            .enter()
            .append('rect')
            .attr('x', (d: any) => this.xScale(d.data.x))
            .attr('y', (d: any) => this.yScale(d[1]))
            .attr('height', (d: any) => this.yScale(d[0]) - this.yScale(d[1]))
            .attr('width', this.xScale.bandwidth());

        this.gpeChart.append('g')
            .attr('class', 'axis')
            .attr('transform', `translate(0,${this.dimensions.boundedHeight})`)
            .call(d3Axis.axisBottom(this.xScale).ticks(1, '%d/%m'));

        this.gpeChart.append('g')
            .attr('class', 'axis')
            .call(d3Axis.axisLeft(this.yScale1).ticks(5, fmt));
    }

    onHovered (isHovered: boolean)
    {
        this.hovered = isHovered;
    }

    onClickEvent = (event: ICoordinates): void =>
    {
        let idxBand = Math.floor(((event.x - this.dimensions.marginLeft) / this.eachBand));

        idxBand = Math.min(idxBand, this.series[0].dataPoints.length - 1);
        this.columnClicked.emit(this.xScale.domain()[idxBand]);
    };

    onMousePosition = ($event: any): void =>
    {
        let idxBand = Math.floor((($event[0] - this.dimensions.marginLeft) / this.eachBand));

        idxBand = Math.min(idxBand, this.series[0].dataPoints.length - 1);

        this.tooltipData = new TooltipData(this.xScale.domain()[idxBand]);
        this.tooltipData.posX = this.xScale(this.xScale.domain()[idxBand]) + this.eachBand;
        this.tooltipData.posY = this.dimensions.height * 0.5;
        this.series.forEach((serie: Serie): number =>
            this.tooltipData.values.push({
                label: serie.label,
                color: serie.config.color,
                value: serie.dataPoints[idxBand].value
            })
        );
    };

    protected initSvg (): void
    {
        this.baseInitSvg();
        this.chartingService.dimensions = this.dimensions;

        this.xScale = d3Scale
            .scaleBand()
            .range([0, this.dimensions.boundedWidth])
            .paddingOuter(0.25)
            .paddingInner(0.25);
        this.yScale = d3Scale
            .scaleLinear()
            .rangeRound([this.dimensions.boundedHeight, 0]);
        this.yScale1 = d3Scale
            .scaleLinear()
            .rangeRound([this.dimensions.boundedHeight, 0]);
        this.zScale = d3Scale
            .scaleOrdinal()
            .range(this.data.colors);
    }
}
